import { useEffect, useRef, type ReactNode } from 'react';
import {
  ComboBox,
  InlineLoading,
  InlineNotification,
  usePrefix,
  type ComboBoxProps,
} from '@carbon/react';
import styles from './srp-combobox.module.scss';

/** How close to the menu bottom (px) triggers the next-page load. */
const LOAD_MORE_THRESHOLD_PX = 48;

/**
 * Props for {@link SRPComboBox}. Extends Carbon's `ComboBoxProps<Item>` so every pass-through
 * prop (`id`, `titleText`, `placeholder`, `disabled`, `size`, `className`, `invalid`/
 * `invalidText`, …) stays in sync with Carbon and needs no re-declaration. The props the
 * wrapper OWNS are `Omit`ted from that surface (so a caller can't bypass the contract) and
 * re-declared below: it intercepts selection/input (`onSelect`/`onSearchChange`), forces no
 * client-side filtering (`shouldFilterItem`), derives `helperText` from `status`, and renders
 * rows via `renderItem` (Carbon's `itemToElement`). `Item` is generic, so `itemToString` and
 * `selectedItem` are required here (Carbon leaves them optional).
 */
export interface SRPComboBoxProps<Item> extends Omit<
  ComboBoxProps<Item>,
  // Carbon/Downshift props the wrapper replaces or hardcodes:
  | 'onChange'
  | 'onInputChange'
  | 'shouldFilterItem'
  | 'itemToElement'
  | 'helperText'
  // Re-declared below as required (generic `Item`):
  | 'itemToString'
  | 'selectedItem'
  // Native DOM `onSelect` (from `InputHTMLAttributes`) — reused here for selection.
  | 'onSelect'
> {
  /** Stringifies an item for the input value + selection restore (required for a generic list). */
  itemToString: (item: Item | null) => string;
  /** The controlled selection. Carbon compares it by deep `isEqual`, so pass the same object shape as its matching `items` entry. */
  selectedItem: Item | null;
  /** Optional custom row renderer (maps to Carbon's `itemToElement`). */
  renderItem?: (item: Item) => ReactNode;
  /** Fired when the selection changes (wraps Carbon's `onChange`). */
  onSelect: (item: Item | null) => void;
  /** Fired when the search text changes (wraps `onInputChange`; caller client-filters or fetches). */
  onSearchChange: (text: string) => void;
  /** Aggregate load state driving the helper/error/loading presentation. */
  status: 'loading' | 'ready' | 'empty' | 'error';
  /** Message shown when `status === 'error'`. */
  errorMessage?: string;
  /** Error-notification title (default "Could not load results"). */
  errorTitle?: string;
  /** Helper shown when `status === 'loading'` (default "Loading…"). */
  loadingText?: string;
  /** Helper shown when `status === 'empty'`. */
  emptyText?: string;
  /** When provided, enables infinite scroll: called near the menu bottom. */
  onLoadMore?: () => void;
  /** Whether another page exists (gates `onLoadMore`). */
  hasNextPage?: boolean;
  /** Whether the next page is currently loading (gates `onLoadMore` + shows a spinner). */
  isFetchingNextPage?: boolean;
}

/**
 * A generic, presentational, fully-controlled Carbon `ComboBox` — renders EXACTLY the
 * `items` it's handed and reports search/select/load-more. It never filters or fetches; the
 * caller supplies items (client-filtered or server-fetched), the selection, and `status`.
 * Every other Carbon `ComboBox` prop passes straight through (`id` is required, plus
 * `titleText`, `placeholder`, `disabled`, `size`, …); `disabled` is caller-controlled (NOT
 * derived from `status`) so a server-searched picker stays typeable.
 * Infinite scroll (opt-in via `onLoadMore`) uses a single capturing scroll listener on the
 * list-box menu, so the menu's own children stay Carbon-managed (mutating them — e.g. a
 * portaled "loading" row — corrupts Downshift's reconciliation and closes the menu).
 *
 * @param {SRPComboBoxProps} props - The controlled items, selection, status, and callbacks.
 * @return {JSX.Element} The combobox.
 */
export function SRPComboBox<Item>({
  itemToString,
  renderItem,
  selectedItem,
  onSelect,
  onSearchChange,
  status,
  errorMessage,
  errorTitle = 'Could not load results',
  loadingText = 'Loading…',
  emptyText,
  onLoadMore,
  hasNextPage = false,
  isFetchingNextPage = false,
  ...rest
}: SRPComboBoxProps<Item>) {
  const prefix = usePrefix();
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Defensive guard so only real typing reports a search. Carbon (1.103) fires
  // `onInputChange` from a post-commit effect — never on mount, and on selection the
  // controlled `selectedItem` prop has already updated in the same batch, so the echo is
  // caught by the guard below. This ref keeps us correct across Carbon versions / an async
  // selection update, where the post-commit fire could precede the prop catching up.
  const firstRender = useRef(true);
  useEffect(() => {
    firstRender.current = false;
  }, []);

  // Infinite scroll — a single capturing listener catches the menu's (non-bubbling) scroll
  // without mounting anything inside Carbon's menu. Attached only when `onLoadMore` is set.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || !onLoadMore) {
      return undefined;
    }
    const menuClass = `${prefix}--list-box__menu`;
    const handleScroll = (event: Event) => {
      const el = event.target as HTMLElement | null;
      if (!el?.classList?.contains(menuClass)) {
        return;
      }
      if (!hasNextPage || isFetchingNextPage) {
        return;
      }
      if (
        el.scrollTop + el.clientHeight >=
        el.scrollHeight - LOAD_MORE_THRESHOLD_PX
      ) {
        onLoadMore();
      }
    };
    wrapper.addEventListener('scroll', handleScroll, { capture: true });
    return () =>
      wrapper.removeEventListener('scroll', handleScroll, { capture: true });
  }, [prefix, onLoadMore, hasNextPage, isFetchingNextPage]);

  const helperText =
    status === 'loading'
      ? loadingText
      : status === 'empty'
        ? (emptyText ?? 'No results found')
        : undefined;

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      {status === 'error' && (
        <InlineNotification
          kind="error"
          title={errorTitle}
          subtitle={errorMessage}
          hideCloseButton
          lowContrast
          className={styles.notification}
        />
      )}
      <ComboBox<Item>
        {...rest}
        itemToString={itemToString}
        itemToElement={renderItem}
        selectedItem={selectedItem}
        helperText={helperText}
        // The component never filters — the caller owns search (client-filter or fetch).
        shouldFilterItem={() => true}
        onChange={({ selectedItem: next }) => onSelect(next ?? null)}
        onInputChange={(text) => {
          const value = text ?? '';
          // Skip mount + selection-echo re-fires so we only report real typing.
          if (firstRender.current) {
            return;
          }
          if (selectedItem && value === itemToString(selectedItem)) {
            return;
          }
          onSearchChange(value);
        }}
      />
      {isFetchingNextPage && (
        <InlineLoading
          description="Loading more…"
          className={styles.loadingMore}
        />
      )}
    </div>
  );
}
